<template>
  <Teleport to="body">
    <!-- No <transition> wrapper. The opacity transition here was leaving the overlay stuck
           between its enter and leave classes, so the leave never completed and Vue never
           unmounted the element -- the modal could not be closed by the X, Cancel, or Escape.
           A modal should appear immediately anyway, so the animation is not worth the failure
           mode. -->
      <div
        v-if="open"
        :class="zIndex"
        class="fixed inset-0 flex items-center justify-center overflow-y-auto bg-black/40 p-4"
        @click.self="dismiss"
      >
        <div
          class="app-modal w-full rounded-lg border border-gray-200 bg-white shadow-xl"
          :class="maxWidth"
          role="dialog"
          aria-modal="true"
          @click.stop
        >
          <!-- Header -->
          <div v-if="!hideHeader" class="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <slot name="header">
              <h3 class="text-lg font-semibold text-gray-900">{{ title }}</h3>
            </slot>
            <button
              v-if="!hideClose"
              type="button"
              class="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Close"
              @click="cancel"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-x">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M18 6l-12 12" />
                <path d="M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Body -->
          <div class="px-6 py-4">
            <slot></slot>
          </div>

          <!-- Footer -->
          <div v-if="!hideFooter" class="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-4">
            <slot name="footer">
              <button
                type="button"
                class="inline-flex min-w-20 items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                @click="cancel"
              >
                {{ cancelLabel }}
              </button>
              <button
                type="button"
                :disabled="primaryDisabled"
                class="inline-flex min-w-20 items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-white transition-colors focus:outline-none focus:ring-2"
                :class="primaryButtonClasses"
                @click="submit"
              >
                {{ primaryLabel }}
              </button>
            </slot>
          </div>
        </div>
      </div>
  </Teleport>
</template>

<script>
export default {
  name: "AppModal",
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    maxWidth: {
      type: String,
      default: 'max-w-md',
    },
    primaryLabel: {
      type: String,
      default: 'Save',
    },
    // 'primary' (blue) or 'danger' (red) styling for the built-in primary button.
    primaryVariant: {
      type: String,
      default: 'primary',
    },
    cancelLabel: {
      type: String,
      default: 'Cancel',
    },
    primaryDisabled: {
      type: Boolean,
      default: false,
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    hideFooter: {
      type: Boolean,
      default: false,
    },
    hideClose: {
      type: Boolean,
      default: false,
    },
    // When true, backdrop click and Escape do NOT dismiss (use for confirm-critical modals).
    persistent: {
      type: Boolean,
      default: false,
    },
    zIndex: {
      type: String,
      default: 'z-50',
    },
  },
  emits: ['update:open', 'submit', 'cancel', 'close'],
  computed: {
    primaryButtonClasses() {
      if (this.primaryDisabled) {
        const base = this.primaryVariant === 'danger' ? 'bg-red-600' : 'bg-blue-600';
        return `${base} opacity-50 cursor-not-allowed focus:ring-blue-500`;
      }

      return this.primaryVariant === 'danger'
        ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
        : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500';
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    close() {
      this.$emit('update:open', false);
      this.$emit('close');
    },
    // Accidental dismissal only -- backdrop click and Escape. `persistent` blocks these so a
    // confirm-critical modal cannot be lost by a stray click. The X button is a deliberate
    // dismissal and routes through cancel() instead, so it always works.
    dismiss() {
      if (this.persistent) {
        return;
      }
      this.close();
    },
    cancel() {
      this.$emit('cancel');
      this.close();
    },
    submit() {
      if (this.primaryDisabled) {
        return;
      }
      this.$emit('submit');
    },
    onKeydown(event) {
      if (this.open && event.key === 'Escape') {
        this.dismiss();
      }
    },
  },
}
</script>

<style scoped>
/*
 * Neutralize the global app-shift.css heading styles (h1,h2,h3 { font-size:1.5rem;
 * padding:25px 0; text-align:center }) so modal titles render as intended without
 * every consumer having to re-override them.
 */
.app-modal :deep(h1),
.app-modal :deep(h2),
.app-modal :deep(h3),
.app-modal :deep(h4) {
  margin: 0;
  padding: 0;
  text-align: inherit;
}
</style>
